import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Model from "@tripian/model";
import { api } from "@tripian/core";
import { FormTemplateLogin } from "@tripian/react";

import {
  DELETE_USER,
  LOGIN,
  REGISTER,
  TRIPS,
  TRIP_CLONE,
  USE_TRIP_URL,
  VISA_AUTH_REDIRECT,
  TRIP_PLAN,
} from "../../constants/ROUTER_PATH_TITLE";
import { saveLocalStorageToken } from "../../App/AppWrapper/localStorages";

import useAuth from "../../hooks/useAuth";
import useUser from "../../hooks/useUser";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import useTranslate from "../../hooks/useTranslate";
import { useGuestUser } from "../../hooks/useGuestUser";

import { Navbar } from "../../components/Navbar/Navbar";
import tripianLogo from "../../assets/img/Tripian_Logo.png";
import classes from "./Login.module.scss";
import ValaiLogin from "../ValaiLanding/ValaiLogin";

const DefaultLoginPage = () => {
  const { isLoggedIn, loggedIn } = useAuth();
  const { user, userFetch } = useUser();
  const { t } = useTranslate();
  const isGuest = useGuestUser();

  const navigate = useNavigate();
  const location = useLocation();

  const useQuery = <T extends { [K in keyof T]?: string } = {}>(): T => {
    const query = new URLSearchParams(location.search);
    const result: T = {} as T;
    query.forEach((val, key) => {
      result[key] = val;
    });
    return result;
  };
  const { sharedTripHash } = useQuery<{ sharedTripHash: string | undefined }>();

  const { getLocalData } = useLocalStorage<"light" | "dark">("theme");
  const theme = getLocalData();

  document.title = LOGIN.TITLE(t("user.login"));

  useEffect(() => {
    if (isGuest && user?.userType === -1) {
      return;
    }

    if (!isLoggedIn) {
      if (!window.tconfig.SHOW_LOGIN) {
        navigate(USE_TRIP_URL.PATH, { replace: true });
      }
      return;
    }

    // User is logged in
    if (!user) return;

    const isVisaFlow =
      window.tconfig.ELIGIBILITY_TYPE === "visa" &&
      user.userType !== Model.USER_TYPE.ELIGIBILITY;

    // If there is visa flow, redirect to VisaAuthRedirect page
    if (isVisaFlow) {
      const queryParams = sharedTripHash
        ? `?sharedTripHash=${sharedTripHash}`
        : "";
      navigate(`${VISA_AUTH_REDIRECT.PATH}${queryParams}`, {
        replace: true,
        state: { visaSource: "login" },
      });
      return;
    }

    const fromTripPlan: boolean | undefined =
      location.state &&
      (location.state as { fromTripPlan?: boolean })?.fromTripPlan;
    const tripHash: string | undefined =
      location.state && (location.state as { tripHash?: string })?.tripHash;

    // If not Visa, continue with the login redirect logic
    if (sharedTripHash) {
      navigate(`${TRIP_CLONE.PATH}/${sharedTripHash}`, { replace: true });
    } else if (
      location.state &&
      (location.state as { from: string })?.from === DELETE_USER.PATH
    ) {
      navigate(DELETE_USER.PATH, { replace: true });
    } else if (fromTripPlan && tripHash) {
      navigate(`${TRIP_PLAN.PATH}/${tripHash}/0`, { replace: true });
    } else {
      navigate(TRIPS.PATH, { replace: true });
    }
  }, [isLoggedIn, user, sharedTripHash, location, navigate, isGuest]);

  const callbackSuccessLogin = async (token: Model.Token) => {
    saveLocalStorageToken(token);

    loggedIn();
    await userFetch(true);
  };

  if (isLoggedIn && !isGuest) return null;

  const isVisa = window.tconfig.ELIGIBILITY_TYPE === "visa";

  return (
    <div
      className={`${classes.loginPage} ${isVisa ? classes.visaAuth : ""}`}
    >
      <Navbar theme={theme} t={t} />
      <div className="full-center-x">
        <FormTemplateLogin
          login={(email, password, captchaToken) =>
            api.loginEmail(email, password, undefined, captchaToken)
          }
          successLogin={callbackSuccessLogin}
          reCaptchaSiteKey={window.tconfig.RECAPTCHA_SITE_KEY}
          showCaptcha={process.env.NODE_ENV === "production"}
          showRegister={window.tconfig.SHOW_REGISTER}
          socialLoginConfigs={{
            clientId: window.tconfig.COGNITO.CLIENT_ID,
            domain: window.tconfig.COGNITO.DOMAIN,
            identityProviders: window.tconfig.COGNITO.IDENTITY_PROVIDERS,
            region: window.tconfig.COGNITO.REGION,
            baseDomain: `${window.tconfig.DOMAIN_ORIGIN}${window.tconfig.DOMAIN_ROUTER_BASE_NAME}`,
          }}
          forgotPassword={() => navigate("/forgot-password")}
          signUpButtonCallBack={() => {
            if (sharedTripHash) {
              navigate(`/register?sharedTripHash=${sharedTripHash}`, {
                replace: true,
              });
            } else if (isGuest) {
              navigate(REGISTER.PATH, {
                replace: true,
                state: { fromCloneModal: true },
              });
            } else {
              navigate("/register", { replace: true });
            }
          }}
          customQuery={sharedTripHash}
          subtitle={isVisa ? t("visa.auth.eligibilityLine") : undefined}
          socialLoginNote={isVisa ? t("visa.auth.socialLoginNote") : undefined}
          t={t}
        />
        <a
          className={classes.loginPoweredBy}
          target="_blank"
          rel="noreferrer"
          href="https://www.tripian.com/"
        >
          <span>Powered by</span>
          <img src={tripianLogo} alt="" />
        </a>
      </div>
      {window.tconfig.IMAGE_PATHS.FORM_BG_IMG_URL && (
        <img
          className={`${classes.loginAppBackground} hide-s`}
          src={window.tconfig.IMAGE_PATHS.FORM_BG_IMG_URL}
          alt={window.tconfig.BRAND_NAME}
        />
      )}
    </div>
  );
};

// Wrapper component to conditionally render ValaiLogin or DefaultLoginPage
const LoginPage = () => {
  if (window.tconfig.LANDING_PAGE === "ValaiLanding") {
    return <ValaiLogin />;
  }
  return <DefaultLoginPage />;
};

export default LoginPage;